import {
	useEffectEvent,
	useEffect,
	useLayoutEffect,
	useReducer,
	useRef,
	type CSSProperties,
	type MouseEventHandler,
} from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { getEngageBaseUrl } from 'lib/engage';
import cn from 'classnames';
import { Link } from 'src/navigation';
import NavItem from 'components/global/global-navigation/nav-item';
import SearchSection from 'components/global/global-navigation/search-section';
import MetLogo from 'assets/icons/met-logo.svg';
import LanguageList from '../../language-list';
import { NavLink } from 'components/global/global-navigation/nav-link';
import { SearchButton } from 'components/global/global-navigation/search-button';
import { HamburgerMenuButton } from 'components/global/global-navigation/hamburger-menu-button';
import { TicketLink } from 'components/homepage/ticket-link';
import { INITIAL_MOBILE_NAV_STATE, mobileNavReducer } from './reducer';
import type { GlobalNavigationCategory } from '../types';
import useMediaQuery from 'hooks/use-media-query';
import { BREAKPOINT_M } from 'lib/constants.mjs';
import styles from './mobile-navbar.module.scss';

function MobileNavbar({
	categories,
	navigationMembershipCode,
	navigationBuyTicketsCode,
	mode = 'inline',
}: GlobalNavigationProps) {
	const [
		{
			isSticky,
			isHidden,
			openNavItem,
			showSearch,
			isMenuOpen,
			isScrolled,
			topOffset,
			areTransitionsSuspended,
		},
		dispatch,
	] = useReducer(mobileNavReducer, INITIAL_MOBILE_NAV_STATE);
	const navRef = useRef<HTMLElement | null>(null);
	const lastPositionRef = useRef({
		scrollY: 0,
		isSticky: false,
	});
	const { matches: isDesktopViewport } = useMediaQuery(
		`(min-width: ${BREAKPOINT_M}px)`,
	);
	const t = useTranslations('navigation');
	const locale = useLocale();

	const updateNavPosition = useEffectEvent(() => {
		const { scrollY: lastScrollY, isSticky: lastIsSticky } =
			lastPositionRef.current;

		// This accounts for the additional offset required when a global
		// notification banner is present at the top of the page.
		const bodyContainer = document.getElementById('page-container');
		const topY = Math.max(
			0,
			(bodyContainer?.getBoundingClientRect().top ?? 0) + window.pageYOffset,
		);
		const navHeight = parseInt(
			window
				.getComputedStyle(document.body)
				.getPropertyValue('--masthead-height'),
		);
		const scrollY = window.scrollY;

		// We enable sticky only once the navbar is out of the viewport. Once enabled, the
		// nav stays sticky until we scroll to the very top. This prevents a jump when scrolling
		// up and intersecting the non-sticky navbar position as well as not enabling sticky state
		// when the user is scrolling up but the non-sticky navbar is already in view.
		const nextIsSticky = lastIsSticky
			? scrollY > topY
			: scrollY > navHeight + topY;

		dispatch({
			type: 'nav_position_updated',
			isSticky: nextIsSticky,
			isHidden: scrollY > lastScrollY,
			isScrolled: scrollY > topY,
			topOffset: Math.max(0, topY - scrollY),
		});

		lastPositionRef.current = { scrollY, isSticky: nextIsSticky };
	});

	// Close all open menus when the user clicks or focuses outside of the navbar.
	// By scoping this to `page-container`, we avoid closing the menu when the user clicks
	// either the "Skip to main content" button or the global notification banner.
	const handleOutsideEvent = useEffectEvent((e: MouseEvent | FocusEvent) => {
		const pageContainer = document.getElementById('page-container');

		if (
			e.target instanceof Node &&
			pageContainer?.contains(e.target) &&
			!navRef?.current?.contains(e.target)
		) {
			dispatch({ type: 'clicked_or_focused_outside' });
		}
	});

	// Ensure that we calculate the correct position of the navbar before the browser paints the next frame.
	useLayoutEffect(() => {
		if (!navRef.current) return;

		updateNavPosition();
	}, [mode]);

	// Update the navbar position when the user scrolls or resizes the window.
	useEffect(() => {
		if (!navRef.current) return;

		let reqId = null;
		const scheduleNavPositionUpdate = () => {
			// Ensure we don't fire the update logic more than once per animation
			// frame (the user's screen's refresh rate).
			if (reqId !== null) return;

			reqId = requestAnimationFrame(() => {
				updateNavPosition();
				reqId = null;
			});
		};

		window.addEventListener('scroll', scheduleNavPositionUpdate, {
			passive: true,
		});
		window.addEventListener('resize', scheduleNavPositionUpdate, {
			passive: true,
		});

		return () => {
			if (reqId !== null) {
				cancelAnimationFrame(reqId);
			}

			window.removeEventListener('scroll', scheduleNavPositionUpdate);
			window.removeEventListener('resize', scheduleNavPositionUpdate);
		};
	}, [mode]);

	// We disable transitions for just a couple of frames when we toggle submenus
	// of the navbar since they open and close instantly.
	useEffect(() => {
		if (!areTransitionsSuspended) return;

		// This shouldn't need throttling because the triggers are user click
		// interactions that won't be happening in rapid succession.
		let reqId = requestAnimationFrame(() => {
			reqId = requestAnimationFrame(() => {
				dispatch({ type: 'transitions_resumed' });
				reqId = null;
			});
		});

		return () => {
			if (reqId === null) return;

			cancelAnimationFrame(reqId);
		};
	}, [areTransitionsSuspended]);

	// Close all open menus when the user clicks or focuses outside of the navbar.
	useEffect(() => {
		document.addEventListener('click', handleOutsideEvent);
		document.addEventListener('focusin', handleOutsideEvent);

		return () => {
			document.removeEventListener('click', handleOutsideEvent);
			document.removeEventListener('focusin', handleOutsideEvent);
		};
	}, []);

	// The mobile navbar is hidden via CSS once the viewport grows past the desktop
	// breakpoint, but its state otherwise persists. Close the mobile menu so that it
	// doesn't remain open when the user shrinks the viewport back down to mobile.
	useEffect(() => {
		if (!isDesktopViewport) return;

		dispatch({ type: 'desktop_viewport_entered' });
	}, [isDesktopViewport]);

	const toggleSearchVisibility = (isVisible?: boolean) => {
		dispatch({ type: 'toggled_search', showSearch: isVisible });
	};

	const handleSearchClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		toggleSearchVisibility();
	};

	const toggleMenu: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		dispatch({ type: 'toggled_mobile_menu' });
	};

	const handleNavItemClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		e.preventDefault();
		dispatch({
			type: 'toggled_subnav_for_item',
			navId: e.currentTarget.dataset.navId,
		});
	};

	return (
		<>
			{showSearch && (
				<div
					className={styles.navScrim}
					aria-hidden="true"
					data-testid="nav-scrim"
				/>
			)}
			<header
				className={cn(styles.mobileNavContainer, {
					[styles.overlay]: mode === 'overlay',
					[styles.mobileMenuOpen]: isMenuOpen,
					[styles.sticky]: isSticky,
					[styles.shown]: !isMenuOpen && !isHidden,
					[styles.scrolled]: isScrolled,
					[styles.transitionsSuspended]: areTransitionsSuspended,
				})}
				style={{ '--mobile-nav-top-offset': `${topOffset}px` } as CSSProperties}
				ref={navRef}
				data-lock-page={isMenuOpen}
				data-testid="global-navbar-mobile"
			>
				<div className={styles.mobileNavbar}>
					<div className={styles.mobileNavbarContent}>
						<Link
							title="Homepage"
							href="/"
							className={styles.logoLink}
							data-analytics-id="nav-logo-link"
						>
							<MetLogo data-testid="met-logo" aria-hidden="true" />
						</Link>
						<div className={styles.mobileNavBtns}>
							<TicketLink
								variant="small"
								navigationBuyTicketsCode={navigationBuyTicketsCode}
								className={cn(styles.navBtn, styles.ticketLink)}
								data-analytics-id="nav-ticket-link"
							/>
							<SearchButton handleClick={handleSearchClick} />
							<HamburgerMenuButton
								isMenuOpen={isMenuOpen}
								handleClick={toggleMenu}
							/>
						</div>
					</div>
					{isMenuOpen && (
						<div className={styles.mobileNavMenuContainer}>
							<nav
								className={styles.mobileNavMenu}
								data-testid="mobile-nav-menu"
							>
								<ul className={styles.mobileNavMenuGroupLinks}>
									{categories?.map((category) => (
										<NavItem
											handleClick={handleNavItemClick}
											isOpen={category.title === openNavItem}
											key={category.title}
											url={category.url}
											links={category.links}
											orientingCopy={category.orientingCopy}
											title={category.title}
										/>
									))}
								</ul>
								<div className={styles.mobileNavMenuFooter}>
									<div className={styles.mobileNavMenuButtons}>
										<TicketLink
											variant="small"
											navigationBuyTicketsCode={navigationBuyTicketsCode}
											className={styles.ticketLink}
											data-analytics-id="nav-mobile-menu-ticket-link"
										/>
										<NavLink
											text={t('membership')}
											href={`${getEngageBaseUrl(locale)}/members/membership/?promocode=${navigationMembershipCode ?? ''}`}
											className={styles.membershipLink}
											data-analytics-id="nav-mobile-menu-membership-link"
										/>
									</div>
									<LanguageList />
								</div>
							</nav>
						</div>
					)}
				</div>
				{showSearch && <SearchSection toggleVisible={toggleSearchVisibility} />}
			</header>
		</>
	);
}

export { MobileNavbar };

export interface GlobalNavigationProps {
	categories: GlobalNavigationCategory[];
	navigationMembershipCode?: number;
	navigationBuyTicketsCode?: number;
	mode?: 'overlay' | 'inline';
}
